/**
 * DailyRoutine — Routine selection and player page for prospects.
 *
 * Shows available routines (public + custom), lets user start one,
 * then mounts RoutinePlayer for the full exercise sequence.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import RoutinePlayer from '@/components/kinetic/RoutinePlayer';

const KTS_URL = import.meta.env.VITE_KTS_URL || '';

interface Routine {
  id: string;
  name: string;
  description: string | null;
  sport: string | null;
  difficulty: string;
  exercise_count: number;
  steps: Array<{
    step_order: number;
    tool_id: string;
    rest_seconds: number;
    target_reps: number | null;
    exercise_name: string;
    heuristic_rule?: any;
  }>;
}

interface RoutineRun {
  id: string;
  routine_id: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  metadata: Record<string, any>;
  routine: { name: string; description: string | null; difficulty: string } | null;
}

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700',
};

export default function DailyRoutine() {
  const { session } = useAuth();
  const token = session?.access_token || '';

  const [routines, setRoutines] = useState<Routine[]>([]);
  const [history, setHistory] = useState<RoutineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active routine run
  const [activeRun, setActiveRun] = useState<{ runId: string; routineName: string; steps: any[] } | null>(null);

  // Load routines
  const loadRoutines = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [routinesRes, historyRes] = await Promise.all([
        fetch(`${KTS_URL}/api/routines`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${KTS_URL}/api/routines/history?limit=10`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const routinesJson = await routinesRes.json();
      const historyJson = await historyRes.json();

      setRoutines(routinesJson.data || []);
      setHistory(historyJson.data || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRoutines();
  }, [loadRoutines]);

  // Start a routine
  const startRoutine = async (routine: Routine) => {
    try {
      // First fetch full detail with heuristic rules
      const detailRes = await fetch(`${KTS_URL}/api/routines/${routine.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const detailJson = await detailRes.json();
      const fullRoutine = detailJson.data;

      // Start the run
      const startRes = await fetch(`${KTS_URL}/api/routines/${routine.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const startJson = await startRes.json();

      if (!startRes.ok) {
        throw new Error(startJson.error || 'Failed to start routine');
      }

      setActiveRun({
        runId: startJson.data.run_id,
        routineName: routine.name,
        steps: fullRoutine.steps || startJson.data.steps,
      });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // If a routine is active, show the player
  if (activeRun) {
    return (
      <div className="mx-auto max-w-2xl">
        <RoutinePlayer
          runId={activeRun.runId}
          routineName={activeRun.routineName}
          steps={activeRun.steps}
          onClose={() => {
            setActiveRun(null);
            loadRoutines(); // Refresh history
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Daily Routine</h1>
        <p className="mt-1 text-sm text-gray-500">
          Choose a routine and crush it. Each exercise is tracked with AI camera.
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-red-50 p-4 text-center text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Routine cards */}
      {!loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {routines.map((routine) => (
            <div
              key={routine.id}
              className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <h3 className="text-base font-bold text-gray-900">{routine.name}</h3>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DIFFICULTY_COLORS[routine.difficulty] || 'bg-gray-100 text-gray-600'}`}>
                  {routine.difficulty}
                </span>
              </div>

              {routine.description && (
                <p className="text-xs text-gray-500 line-clamp-2">{routine.description}</p>
              )}

              {/* Exercise list */}
              <div className="space-y-1">
                {routine.steps.map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-blue-600">
                      {i + 1}
                    </span>
                    <span className="text-gray-700">{step.exercise_name}</span>
                    {step.target_reps && (
                      <span className="text-gray-400">x{step.target_reps}</span>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => startRoutine(routine)}
                className="w-full rounded-xl bg-blue-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700"
              >
                Start Routine
              </button>
            </div>
          ))}

          {routines.length === 0 && !loading && (
            <div className="col-span-full rounded-2xl bg-gray-50 py-12 text-center">
              <p className="text-gray-400">No routines available yet.</p>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-gray-900">Recent Runs</h2>
          <div className="space-y-2">
            {history.map((run) => {
              const meta = run.metadata || {};
              const exerciseSummary = (meta.exercise_summary || []) as Array<{ exercise: string; reps: number }>;
              const totalReps = meta.total_reps || 0;
              const timeMin = Math.floor((meta.total_time_seconds || 0) / 60);

              return (
                <div
                  key={run.id}
                  className="flex items-center justify-between rounded-xl border border-gray-100 bg-white px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {run.routine?.name || 'Routine'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(run.started_at).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                      {exerciseSummary.length > 0 && (
                        <> — {exerciseSummary.map((e) => `${e.reps} ${e.exercise.toLowerCase()}`).join(', ')}</>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600 tabular-nums">{totalReps}</p>
                    <p className="text-[10px] text-gray-400">
                      {timeMin > 0 ? `${timeMin}m` : '< 1m'} • {run.status}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
